import { useRef, useState, useMemo, useCallback } from 'react';
import { t, Trans } from '@lingui/macro';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import Search from '@decisiv/iconix/lib/components/Search';
import InfoCircle from '@decisiv/iconix/lib/components/InfoCircle';
import QuestionCircleF from '@decisiv/iconix/lib/components/QuestionCircleF';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import OptionsList from '@decisiv/ui-components/lib/atoms/OptionsList';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Button from '@decisiv/ui-components/lib/components/Button';
import {
  FooterActions,
  Selection,
} from '@decisiv/ui-components/lib/atoms/OptionsList/types';

import EmptyResults from 'components/EmptyResults';

import { useManagedDepotsFromPlatformAccountQuery } from 'pages/Management/Account/Depots/hooks/useManagedDepotsFromPlatformAccountQuery';

import DepotCard from '../DepotCard';
import { DepotSelectorWrapper, TextFieldWrapper, HelpText } from './styles';

export interface ContactDepot {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
  platformAccountName?: string | null;
  isFixed?: boolean;
  primaryContact?: boolean | null;
}

interface DepotSelectorProps {
  accountId: string;
  depots: ContactDepot[];
  onDepotsChange: (depots: ContactDepot[]) => void;
  onRemoveDepot: (depotId: string) => void;
  containerSelector?: string;
  primaryDepotIds: string[];
  onPrimaryChange: (depotId: string, checked: boolean) => void;
}

const DepotSelector = ({
  accountId,
  depots,
  onDepotsChange,
  onRemoveDepot,
  containerSelector = '#update-contact-modal',
  primaryDepotIds,
  onPrimaryChange,
}: DepotSelectorProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState<Selection>([]);

  const { data, loading } = useManagedDepotsFromPlatformAccountQuery({
    variables: {
      page: 1,
      platformAccountIds: [accountId],
      text: searchText || undefined,
      sort: { column: 'NAME', direction: 'ASC' },
    },
    skip: !isPopoverVisible || !accountId,
  });

  const depotOptions = useMemo(() => {
    const collection = data?.managedAssetGroupings?.collection ?? [];
    const currentDepotIds = depots.map((d) => d.id);

    return collection
      .filter((d) => !currentDepotIds.includes(d.id))
      .map((d) => ({
        id: d.id,
        label: d.name,
        description: [d.city, d.state].filter(Boolean).join(', '),
      }));
  }, [data, depots]);

  const sortedDepots = useMemo(
    () =>
      [...depots].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [depots],
  );

  const depotDataMap = useMemo(() => {
    const map = new Map<string, ContactDepot>();
    const collection = data?.managedAssetGroupings?.collection ?? [];
    collection.forEach((d) => {
      map.set(d.id, {
        id: d.id,
        name: d.name,
        city: d.city,
        state: d.state,
        platformAccountName: d.platformAccount?.name ?? null,
      });
    });
    return map;
  }, [data]);

  const showPopover = useCallback(() => {
    setIsPopoverVisible(true);
    setSelected([]);
  }, []);

  const hidePopover = useCallback(() => {
    setIsPopoverVisible(false);
    setSearchText('');
    setSelected([]);
  }, []);

  const onApply = useCallback(() => {
    const newDepots = (selected as string[])
      .map((id) => depotDataMap.get(id))
      .filter(Boolean) as ContactDepot[];

    onDepotsChange([...depots, ...newDepots]);
    hidePopover();
  }, [selected, depotDataMap, depots, onDepotsChange, hidePopover]);

  const onCancel = useCallback(() => {
    hidePopover();
  }, [hidePopover]);

  const onClear = useCallback(() => {
    setSelected([]);
  }, []);

  const onChangeSelected = useCallback((ids: Selection) => {
    setSelected(ids);
  }, []);

  const textFieldWidth = targetRef.current
    ? `${targetRef.current.clientWidth}px`
    : 'auto';

  const optionsListActions = {
    left: [
      {
        id: 'apply',
        text: t`Apply`,
        onClick: onApply,
        disabled: selected.length === 0,
        'aria-label': t`Apply selected depots`,
      },
      {
        id: 'cancel',
        text: t`Cancel`,
        onClick: onCancel,
        kind: 'secondary',
        'aria-label': t`Cancel depot selection`,
      },
    ],
    right: [
      {
        id: 'clear',
        text: t`Clear`,
        onClick: onClear,
        variant: 'ghost',
        'aria-label': t`Clear all selected depots`,
      },
    ],
  } satisfies FooterActions;

  const getEmptyMessageTitle = () => {
    if (loading) return t`Loading...`;
    if (searchText.length > 0) return t`No Matches for "${searchText}"`;
    return t`No Results Found`;
  };

  const getEmptyMessageSubtitle = () => {
    if (loading) return '';
    if (searchText.length > 0)
      return t`Check that all words are spelled correctly or search by a different value.`;
    return t`There are no depots available.`;
  };

  return (
    <DepotSelectorWrapper>
      <TextFieldWrapper ref={targetRef}>
        <TextField
          label={t`Depots`}
          aria-label={t`Search depots`}
          value={searchText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchText(e.target.value)
          }
          onFocus={showPopover}
        />
        <ChevronDown className="chevron-icon" />
      </TextFieldWrapper>
      <Flex flexDirection="column" marginTop={0.5} style={{ gap: '8px' }}>
        <HelpText>
          <QuestionCircleF size="small" color="rgba(0,0,0,0.3)" />
          <P size="small" shade={1}>
            <Trans>Search for depots to associate with this contact.</Trans>
          </P>
        </HelpText>

        <Popover
          showArrow={false}
          visible={isPopoverVisible}
          target={targetRef}
          containerSelector={containerSelector}
        >
          <Flex
            flex={1}
            flexDirection="column"
            style={{ minWidth: textFieldWidth }}
          >
            {depotOptions.length > 0 ? (
              <OptionsList
                multiple
                name="depot-selector"
                items={depotOptions}
                selected={selected}
                minWidth={textFieldWidth}
                onChange={onChangeSelected}
                actions={optionsListActions}
              />
            ) : (
              <Flex flexDirection="column" padding={1} style={{ gap: '8px' }}>
                <EmptyResults
                  size="medium"
                  icon={Search}
                  title={getEmptyMessageTitle()}
                  subtitle={getEmptyMessageSubtitle()}
                />
                <Flex justifyContent="center">
                  <Button
                    size="small"
                    kind="secondary"
                    text={t`Close`}
                    onClick={hidePopover}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
        </Popover>

        {depots.length === 0 && (
          <EmptyResults
            maxWidth="100%"
            color="licoriceMousse"
            title={t`This contact is not included in any depots yet`}
            subtitle={t``}
            icon={InfoCircle}
            size="small"
            iconSize="medium"
          />
        )}

        {sortedDepots.map((d) => (
          <DepotCard
            key={d.id}
            depot={d}
            onRemove={onRemoveDepot}
            isPrimary={primaryDepotIds.includes(d.id)}
            onPrimaryChange={onPrimaryChange}
          />
        ))}
      </Flex>
    </DepotSelectorWrapper>
  );
};

export default DepotSelector;
